import { useEffect, useRef, useState } from "react";
import { useHistory } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { displayToast } from "../../../components/app/AppToast";
import {
  lookupExistingProvider,
  loginWithEmailOtp,
} from "../../../services/firebase";
import { otpCredentialStore } from "../stores/otpCredentialStore";
import { updateOnboardingFlow } from "../stores/onboardingFlowStore";
import {
  desktopCodeRequestStore,
  type DesktopCodeRequest,
} from "../stores/desktopCodeRequestStore";
import { useEmailOtp, type EmailOtpCredential } from "./useEmailOtp";
import {
  gaEventAuthFailed,
  gaEventSignupSheetOpened,
  resolveAuthErrorCode,
  type OtpFailureReason,
} from "../../../tools/analytics/domains/onboarding.analytics";

/**
 * The desktop front door, second half: the code, in the modal over the app
 * (Figma "D7.inscription.default"). The address was taken on `/signup` or
 * `/login` (`useDesktopLogin`), which decided the intent and handed it over in
 * `request`.
 *
 * What it does with a verified code:
 *
 * - **login** → `loginWithEmailOtp`, which signs in and routes through
 *   `loginAndRedirect` exactly as the mobile login page does, to the page the
 *   logged-out guard memorized. This hook decides nothing there on purpose: a
 *   second opinion about where a login lands is how two doors drift apart.
 * - **signup** → hand the proof to onboarding WITHOUT signing in, and let its
 *   name screen create the account. A Firebase session for a uid with no PG row
 *   takes a 401 on `/users/me` and on every other private route.
 */
export function useDesktopAuthGate(request: DesktopCodeRequest): {
  code: string;
  setCode: (code: string) => void;
  isBusy: boolean;
  /**
   * Why the code on screen was refused. It stays with THAT code: the digits
   * stay up in red (Figma "D7.inscription.code-expire"), and typing another
   * code takes the reason away with them.
   */
  error: OtpFailureReason | null;
  cooldownRemaining: number;
  onCodeComplete: (code: string) => void;
  onResend: () => void;
  onChangeEmail: () => void;
} {
  const history = useHistory();
  const { t } = useTranslation();

  const [code, setCode] = useState("");
  const [refused, setRefused] = useState<{
    code: string;
    reason: OtpFailureReason;
  } | null>(null);
  const otp = useEmailOtp({ intent: request.intent, surface: request.surface });

  /**
   * Ask for the code the modal is about to wait for.
   *
   * Arriving IS the request: there is no button to press between the address
   * and needing a code. Guarded on the request so a re-render never doubles
   * it. A new request (the visitor went back to the door and came again, even
   * with the same address, which is what someone who mistyped and corrected
   * themselves does) is a new object, so it asks again.
   */
  const requestedForRef = useRef<DesktopCodeRequest | null>(null);
  useEffect(() => {
    if (requestedForRef.current === request) return;
    requestedForRef.current = request;
    void (async () => {
      const outcome = await otp.requestCode(request.email);
      // A trusted device never waits for a mail. The six boxes stay on screen
      // for the instant it takes to sign in or to hand onboarding the proof.
      if (outcome.outcome === "trusted") await completeAuth(outcome);
    })();
  }, [request, otp]);

  function onCodeComplete(submitted: string): void {
    void (async () => {
      const result = await otp.verifyCode(request.email, submitted);
      if (result === null) {
        displayToast(t("Something went wrong. Please try again."), "error");
        return;
      }
      if (result.outcome === "rejected") {
        setRefused({ code: submitted, reason: result.reason });
        return;
      }

      await completeAuth(result);
    })();
  }

  /**
   * Spend a proved address, however it was proved. Shared by the code path and
   * the trusted-device one, because the two differ in what they had to ask the
   * practitioner for and in nothing that happens afterwards.
   */
  async function completeAuth(credential: EmailOtpCredential): Promise<void> {
    if (request.intent === "login") {
      // Signs in and routes to the memorized page. A refused sign-in says so,
      // as the mobile login does: the code is spent, and the modal would
      // otherwise sit there with nothing explained.
      const result = await loginWithEmailOtp(credential.customToken, null);
      if (result.isSuccess) {
        desktopCodeRequestStore.set(null);
        return;
      }
      gaEventAuthFailed({
        code: resolveAuthErrorCode(result.errorCode),
        method: "email",
        intent: "login",
        surface: request.surface,
        existing_provider: await lookupExistingProvider(request.email),
      });
      displayToast(t("Something went wrong. Please try again."), "error");
      return;
    }

    // Signup: nobody is signed in and no PG row exists yet. Hand the proof to
    // onboarding, which creates the account on its name screen.
    otpCredentialStore.set({
      customToken: credential.customToken,
      uid: credential.uid,
      email: request.email,
    });
    updateOnboardingFlow({
      otpVerified: true,
      email: request.email,
      // The legal line on the door certifies it, which is why the intro slide
      // and its gate do not run on desktop. Left null, the France-only RPPS
      // lookup would silently never happen.
      isHealthcareProfessional: true,
      entrance: "desktop-gate",
    });
    history.push("/signup/onboarding");
    desktopCodeRequestStore.set(null);
  }

  // The refused digits go with the refusal: the boxes are full otherwise, so
  // the new code could not be typed, and Continue would send the refused one
  // again.
  function onResend(): void {
    setRefused(null);
    setCode("");
    void otp.resendCode(request.email);
  }

  // Back to the address, pre-filled, for the mistyped one that will never
  // receive anything. On the door that matches what `check-email` said of it.
  function onChangeEmail(): void {
    otp.reset();
    desktopCodeRequestStore.set(null);
    gaEventSignupSheetOpened({
      surface: request.surface,
      is_invited_to_room: false,
    });
    history.push({
      pathname: request.intent === "signup" ? "/signup" : "/login",
      state: { email: request.email, surface: request.surface },
    });
  }

  return {
    code,
    setCode,
    isBusy: otp.state.status === "sending" || otp.state.status === "verifying",
    // A refused send (too many requests) belongs to no code.
    error:
      refused === null
        ? otp.state.status === "awaiting_code"
          ? otp.state.error
          : null
        : refused.code === code
          ? refused.reason
          : null,
    cooldownRemaining: otp.cooldownRemaining,
    onCodeComplete,
    onResend,
    onChangeEmail,
  };
}
